import { useState } from "react";
import { motion } from "framer-motion";
import { certificationsData } from "../data";

function Certificates() {
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const handleCertificateClick = (index) => {
    setSelectedCertificate(index === selectedCertificate ? null : index);
  };

  return (
    <section className="py-20 bg-gray-50" id="certificates">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={containerVariants}
          className="max-w-6xl mx-auto"
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <span className="text-primary font-medium">
              Education & Credentials
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mt-2">
              Certificates & Qualifications
            </h2>
            <div className="w-20 h-1 bg-primary mx-auto mt-4 rounded-full"></div>
            <p className="text-gray-600 mt-6 max-w-2xl mx-auto">
              I'm committed to continuous learning and professional development.
              Here are some of my certifications and educational achievements.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6">
            {certificationsData.map((certificate, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                onClick={() => handleCertificateClick(index)}
                className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer border border-gray-100 overflow-hidden ${
                  selectedCertificate === index ? "ring-2 ring-primary" : ""
                }`}
              >
                <div className="p-6 flex flex-col md:flex-row items-start md:items-center justify-between">
                  <div className="mb-4 md:mb-0">
                    <h3 className="text-xl font-semibold text-dark group-hover:text-primary transition-colors duration-300">
                      {certificate.title}
                    </h3>
                    <div className="flex items-center mt-2">
                      <span className="text-gray-600">
                        {certificate.institution}
                      </span>
                      <span className="mx-2 text-gray-400">•</span>
                      <span className="text-gray-500 text-sm">
                        {certificate.date}
                      </span>
                    </div>
                    <p className="text-gray-600 mt-2">
                      {certificate.description}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <a
                      href={certificate.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline text-sm py-2 px-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View Certificate
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 ml-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                    <span className="ml-4 text-gray-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-6 w-6 transition-transform duration-300 ${
                          selectedCertificate === index ? "rotate-180" : ""
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
                {selectedCertificate === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-6"
                  >
                    <div className="w-full h-64 rounded-lg overflow-hidden border border-gray-200">
                      <iframe
                        src={certificate.pdf}
                        title={certificate.title}
                        className="w-full h-full"
                      ></iframe>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div variants={itemVariants} className="mt-12 text-center">
            <p className="text-gray-600">
              In addition to these certificates, I'm constantly expanding my
              knowledge through self-study, online courses, and practical
              project experience.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default Certificates;
